import React from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Play,
  Square,
  Activity,
  Clock,
  User,
  FileText,
  Keyboard,
  Users,
} from "lucide-react";
import { useSubmissionNavigation } from "../contexts/SubmissionNavigationContext";
import { toast } from "sonner";

interface SubmissionNavigationControlsProps {
  onOpenSandbox?: () => void;
  showSandboxControls?: boolean;
  className?: string;
}

export function SubmissionNavigationControls({
  onOpenSandbox,
  showSandboxControls = true,
  className = "",
}: SubmissionNavigationControlsProps) {
  const {
    submissions,
    currentIndex,
    currentSubmission,
    goToNext,
    goToPrevious,
    goToSubmission,
    hasNext,
    hasPrevious,
    assignmentTitle,
  } = useSubmissionNavigation();

  const handleSubmissionSelect = (submissionId: string) => {
    const index = submissions.findIndex((s) => s.id === submissionId);
    if (index >= 0) {
      goToSubmission(index);
    }
  };

  if (!currentSubmission) {
    return null;
  }
  // Add this helper near the top of the file
  const statusBadgeClass = (status?: string) => {
    switch (status) {
      case "submitted":
        return "bg-blue-50 text-blue-700";
      case "reviewed":
        return "bg-purple-50 text-purple-700";
      case "completed":
        return "bg-green-50 text-green-700";
      case "returned":
        return "bg-amber-50 text-amber-700";
      default:
        return "bg-gray-50 text-gray-700";
    }
  };

  const displayStatus = (s?: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

  const formatTeamMembersDisplay = (submission: any) => {
    const MAX_LENGTH = 25;
    const MIN_NAME_LENGTH = 4; // Minimum letters to show for a name
    const ELLIPSIS = "...";

    const teamMembers = submission.group?.members || [];
    const submittedUser = submission.studentName || "Unknown User";
    const submittedUserId = submission.studentId;

    // Case: No group members
    if (teamMembers.length === 0) {
      if (submittedUser.length <= MAX_LENGTH) {
        return { names: submittedUser, count: null };
      } else {
        return {
          names: submittedUser.substring(0, MAX_LENGTH) + ELLIPSIS,
          count: null,
        };
      }
    }

    // Get other members (excluding submitted user)
    const otherMembers = teamMembers.filter(
      (member: any) => member.id !== submittedUserId
    );

    // Case: Only submitted user in group
    if (otherMembers.length === 0) {
      if (submittedUser.length <= MAX_LENGTH) {
        return { names: submittedUser, count: null };
      } else {
        return {
          names: submittedUser.substring(0, MAX_LENGTH) + ELLIPSIS,
          count: null,
        };
      }
    }

    // Build the names string with other members
    const names: string[] = [submittedUser];
    let totalLength = submittedUser.length;
    let includedCount = 0;

    for (let i = 0; i < otherMembers.length; i++) {
      const memberName = otherMembers[i].name || "";
      const separator = ", ";
      const separatorLength = separator.length;

      // Check if we can fit the full name
      if (totalLength + separatorLength + memberName.length <= MAX_LENGTH) {
        names.push(memberName);
        totalLength += separatorLength + memberName.length;
        includedCount++;
      } else {
        // Check if we can fit at least MIN_NAME_LENGTH characters
        const availableSpace =
          MAX_LENGTH - totalLength - separatorLength - ELLIPSIS.length;

        if (availableSpace >= MIN_NAME_LENGTH) {
          // Truncate and add this member
          const truncated = memberName.substring(0, availableSpace);
          names.push(truncated);
          totalLength = MAX_LENGTH; // We've used all space
          includedCount++;
          break; // Can't add more
        } else {
          // Not enough space for meaningful name (less than 4 chars), skip this member
          break;
        }
      }
    }

    // Calculate remaining count
    const remainingCount = otherMembers.length - includedCount;

    // Build the display string
    let namesStr = names.join(", ");

    // Add ellipsis if we truncated the last included name
    if (
      includedCount > 0 &&
      totalLength >= MAX_LENGTH &&
      namesStr.length < totalLength
    ) {
      namesStr = namesStr + ELLIPSIS;
    } else if (totalLength > MAX_LENGTH) {
      namesStr = namesStr.substring(0, MAX_LENGTH) + ELLIPSIS;
    }

    const countStr = remainingCount > 0 ? `+${remainingCount}` : null;

    return { names: namesStr, count: countStr };
  };

  return (
    <div className={`border-b border-gray-200 bg-white ${className}`}>
      <div className="px-4 py-4">
        {/* Main Navigation Controls */}
        <div className="flex items-center justify-between">
          {/* Navigation Controls */}
          <div className="flex items-center space-x-4">
            {/* Previous/Next Buttons */}
            <div className="flex items-center space-x-1">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPrevious}
                disabled={!hasPrevious}
                className="flex items-center space-x-1"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Previous</span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={goToNext}
                disabled={!hasNext}
                className="flex items-center space-x-1"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Submission Counter & Selector */}
            <div className="flex items-center space-x-3">
              <Badge variant="outline">
                {currentIndex + 1} of {submissions.length}
              </Badge>

              <Select
                value={currentSubmission.id}
                onValueChange={handleSubmissionSelect}
              >
                <SelectTrigger className="w-[235px]">
                  {" "}
                  {/* Set fixed width */}
                  <SelectValue>
                    <div className="flex items-center gap-2 overflow-hidden">
                      {/* Icon - fixed width */}
                      <div className="flex-shrink-0">
                        {currentSubmission?.group ? (
                          <Users className="h-3 w-3" />
                        ) : (
                          <User className="h-3 w-3" />
                        )}
                      </div>

                      {/* <span className="truncate min-w-0">
                        {formatTeamMembersDisplay(currentSubmission).names}
                      </span>

                      {formatTeamMembersDisplay(currentSubmission).count && (
                        <span className="flex-shrink-0 whitespace-nowrap">
                          {formatTeamMembersDisplay(currentSubmission).count}
                        </span>
                      )} */}
                      <span className="flex-shrink-0 whitespace-nowrap">
                        {currentSubmission.group?.name
                          ? currentSubmission.group.name
                          : currentSubmission.studentName}
                      </span>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {submissions.map((submission) => (
                    <SelectItem key={submission.id} value={submission.id}>
                      <div className="flex items-center justify-between w-full gap-3">
                        {/* Left section - Icon and Names */}
                        <div className="flex items-center min-w-0 flex-1">
                          <div className="flex-shrink-0">
                            {submission?.group ? (
                              <Users className="h-3 w-3 mr-2" />
                            ) : (
                              <User className="h-3 w-3 mr-2" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div>
                              <span className="block truncate">
                                {submission.group?.name
                                  ? submission.group.name
                                  : submission.studentName}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500 truncate block">
                              {submission?.checkpointTitle}
                            </span>
                          </div>
                        </div>

                        {/* Right section - Badge */}
                        <div className="flex-shrink-0">
                          <Badge
                            className={`text-xs px-2 py-0.5 ${statusBadgeClass(
                              submission.status
                            )}`}
                          >
                            {displayStatus(submission.status)}
                          </Badge>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
